import React, { useState } from 'react';
import { UserCheck, Shield, Users, Lock, Eye, EyeOff, Plus, Search, Filter, Settings } from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';

const AccessControls = () => {
  const [activeTab, setActiveTab] = useState('user-permissions');
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Mock data for user permissions
  const userPermissions = [
    {
      id: 1,
      user: 'Sarah Johnson',
      email: 'sarah.johnson@company.com',
      role: 'Contract Manager',
      department: 'Legal',
      permissions: ['Read', 'Write', 'Approve'],
      lastLogin: '2024-01-15 14:30',
      status: 'Active',
      accessLevel: 'Full Access'
    },
    {
      id: 2,
      user: 'Michael Chen',
      email: 'michael.chen@company.com',
      role: 'Legal Counsel',
      department: 'Legal',
      permissions: ['Read', 'Write', 'Approve', 'Admin'],
      lastLogin: '2024-01-15 14:25',
      status: 'Active',
      accessLevel: 'Administrator'
    },
    {
      id: 3,
      user: 'Emily Rodriguez',
      email: 'emily.rodriguez@company.com',
      role: 'Finance Analyst',
      department: 'Finance',
      permissions: ['Read', 'Write'],
      lastLogin: '2024-01-15 14:20',
      status: 'Active',
      accessLevel: 'Standard Access'
    },
    {
      id: 4,
      user: 'David Kim',
      email: 'david.kim@company.com',
      role: 'Procurement Manager',
      department: 'Procurement',
      permissions: ['Read'],
      lastLogin: '2024-01-15 14:15',
      status: 'Inactive',
      accessLevel: 'Read Only'
    }
  ];

  // Mock data for role management
  const roles = [
    {
      id: 1,
      name: 'Administrator',
      description: 'Full system access with administrative privileges',
      permissions: ['Read', 'Write', 'Approve', 'Admin', 'Delete'],
      userCount: 2,
      createdDate: '2024-01-01',
      status: 'Active'
    },
    {
      id: 2,
      name: 'Contract Manager',
      description: 'Manage contracts and related documents',
      permissions: ['Read', 'Write', 'Approve'],
      userCount: 5,
      createdDate: '2024-01-01',
      status: 'Active'
    },
    {
      id: 3,
      name: 'Finance Analyst',
      description: 'Access to financial data and reports',
      permissions: ['Read', 'Write'],
      userCount: 3,
      createdDate: '2024-01-01',
      status: 'Active'
    },
    {
      id: 4,
      name: 'Read Only User',
      description: 'View-only access to documents',
      permissions: ['Read'],
      userCount: 8,
      createdDate: '2024-01-01',
      status: 'Active'
    }
  ];

  // Mock data for access policies
  const accessPolicies = [
    {
      id: 1,
      name: 'Contract Access Policy',
      description: 'Defines access rules for contract documents',
      resourceType: 'Contracts',
      conditions: 'Department = Legal OR Role = Contract Manager',
      permissions: ['Read', 'Write', 'Approve'],
      status: 'Active',
      lastModified: '2024-01-15',
      modifiedBy: 'Michael Chen'
    },
    {
      id: 2,
      name: 'Financial Data Policy',
      description: 'Controls access to financial information',
      resourceType: 'Financial Data',
      conditions: 'Department = Finance OR Role = Administrator',
      permissions: ['Read', 'Write'],
      status: 'Active',
      lastModified: '2024-01-14',
      modifiedBy: 'Sarah Johnson'
    },
    {
      id: 3,
      name: 'System Administration Policy',
      description: 'Administrative access to system settings',
      resourceType: 'System Settings',
      conditions: 'Role = Administrator',
      permissions: ['Read', 'Write', 'Admin', 'Delete'],
      status: 'Active',
      lastModified: '2024-01-13',
      modifiedBy: 'Michael Chen'
    }
  ];

  // Mock data for access logs
  const accessLogs = [
    {
      id: 1,
      user: 'Sarah Johnson',
      action: 'Document Access',
      resource: 'Q4 Contract Template',
      timestamp: '2024-01-15 14:30:25',
      ipAddress: '192.168.1.100',
      status: 'Granted',
      reason: 'User has contract management permissions'
    },
    {
      id: 2,
      user: 'Emily Rodriguez',
      action: 'Report Generation',
      resource: 'Financial Report',
      timestamp: '2024-01-15 14:25:10',
      ipAddress: '192.168.1.102',
      status: 'Granted',
      reason: 'User has finance analyst role'
    },
    {
      id: 3,
      user: 'David Kim',
      action: 'Document Edit',
      resource: 'Vendor Agreement',
      timestamp: '2024-01-15 14:20:45',
      ipAddress: '192.168.1.103',
      status: 'Denied',
      reason: 'User has read-only permissions'
    },
    {
      id: 4,
      user: 'Michael Chen',
      action: 'System Settings',
      resource: 'User Management',
      timestamp: '2024-01-15 14:15:30',
      ipAddress: '192.168.1.101',
      status: 'Granted',
      reason: 'User has administrator role'
    }
  ];

  const tabs = [
    { id: 'user-permissions', name: 'User Permissions', icon: <UserCheck size={18} /> },
    { id: 'role-management', name: 'Role Management', icon: <Users size={18} /> },
    { id: 'access-policies', name: 'Access Policies', icon: <Shield size={18} /> },
    { id: 'access-logs', name: 'Access Logs', icon: <Lock size={18} /> }
  ];

  const handleAction = (action, data) => {
    setModalData({ action, data });
    setShowModal(true);
  };

  const handleConfirm = () => {
    // Handle action confirmation
    setShowModal(false);
    setModalData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      case 'Granted': return 'bg-green-100 text-green-800';
      case 'Denied': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccessLevelColor = (level) => {
    switch (level) {
      case 'Administrator': return 'bg-red-100 text-red-800';
      case 'Full Access': return 'bg-blue-100 text-blue-800';
      case 'Standard Access': return 'bg-green-100 text-green-800';
      case 'Read Only': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderUserPermissions = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {userPermissions.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Users
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {userPermissions.filter(u => u.status === 'Active').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Users
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserCheck size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {userPermissions.filter(u => u.accessLevel === 'Administrator').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Administrators
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#6c757d', fontWeight: '700', marginBottom: '4px' }}>
                    {userPermissions.filter(u => u.accessLevel === 'Read Only').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Read Only Users
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#6c757d20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Eye size={24} style={{ color: '#6c757d' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={userPermissions}
          columns={[
            {
              key: 'user',
              label: 'User',
              width: '200px',
              render: (item) => (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{item.user}</div>
                  <small style={{ color: '#6b7280', fontSize: '11px' }}>{item.email}</small>
                </div>
              )
            },
            {
              key: 'role',
              label: 'Role',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'department',
              label: 'Department',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'permissions',
              label: 'Permissions',
              width: '150px',
              render: (item) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.permissions && Array.isArray(item.permissions) ? item.permissions.map((permission, index) => (
                    <span key={index} className="badge badge-primary" style={{ fontSize: '10px' }}>
                      {permission}
                    </span>
                  )) : <span style={{ fontSize: '12px', color: '#6b7280' }}>No permissions</span>}
                </div>
              )
            },
            {
              key: 'accessLevel',
              label: 'Access Level',
              width: '120px',
              type: 'badge',
              badgeClass: (value) => {
                const levelClasses = {
                  'Administrator': 'badge-danger',
                  'Full Access': 'badge-primary',
                  'Standard Access': 'badge-success',
                  'Read Only': 'badge-secondary'
                };
                return levelClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Active': 'badge-success',
                  'Inactive': 'badge-danger'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'lastLogin',
              label: 'Last Login',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            }
          ]}
          title="User Permissions"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('edit-permissions', item)}
          onDelete={(item) => handleAction('edit-permissions', item)}
          searchPlaceholder="Search users by name, role, or department..."
          loading={false}
          emptyMessage="No users found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderRoleManagement = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {roles.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Roles
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {roles.filter(r => r.status === 'Active').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Roles
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserCheck size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#6f42c1', fontWeight: '700', marginBottom: '4px' }}>
                    {roles.reduce((sum, role) => sum + role.userCount, 0)}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Users
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#6f42c120',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#6f42c1' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {roles.filter(r => r.permissions.includes('Admin')).length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Admin Roles
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={roles}
          columns={[
            {
              key: 'name',
              label: 'Role Name',
              width: '200px',
              render: (item) => (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{item.name}</div>
                  <small style={{ color: '#6b7280', fontSize: '11px' }}>{item.description}</small>
                </div>
              )
            },
            {
              key: 'permissions',
              label: 'Permissions',
              width: '200px',
              render: (item) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.permissions && Array.isArray(item.permissions) ? item.permissions.map((permission, index) => (
                    <span key={index} className="badge badge-primary" style={{ fontSize: '10px' }}>
                      {permission}
                    </span>
                  )) : <span style={{ fontSize: '12px', color: '#6b7280' }}>No permissions</span>}
                </div>
              )
            },
            {
              key: 'userCount',
              label: 'Users',
              width: '80px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{value}</span>
              )
            },
            {
              key: 'createdDate',
              label: 'Created Date',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Active': 'badge-success',
                  'Inactive': 'badge-danger'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            }
          ]}
          title="Role Management"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-users', item)}
          onEdit={(item) => handleAction('edit-role', item)}
          onDelete={(item) => handleAction('edit-role', item)}
          searchPlaceholder="Search roles by name or description..."
          loading={false}
          emptyMessage="No roles found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderAccessPolicies = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {accessPolicies.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Policies
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {accessPolicies.filter(p => p.status === 'Active').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Active Policies
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserCheck size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#6f42c1', fontWeight: '700', marginBottom: '4px' }}>
                    {new Set(accessPolicies.map(p => p.resourceType)).size}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Resource Types
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#6f42c120',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={24} style={{ color: '#6f42c1' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {accessPolicies.filter(p => p.permissions.includes('Admin')).length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Admin Policies
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={accessPolicies}
          columns={[
            {
              key: 'name',
              label: 'Policy Name',
              width: '200px',
              render: (item) => (
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{item.name}</div>
                  <small style={{ color: '#6b7280', fontSize: '11px' }}>{item.description}</small>
                </div>
              )
            },
            {
              key: 'resourceType',
              label: 'Resource Type',
              width: '120px',
              type: 'badge',
              badgeClass: () => 'badge-primary'
            },
            {
              key: 'conditions',
              label: 'Conditions',
              width: '200px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'permissions',
              label: 'Permissions',
              width: '150px',
              render: (item) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {item.permissions && Array.isArray(item.permissions) ? item.permissions.map((permission, index) => (
                    <span key={index} className="badge badge-success" style={{ fontSize: '10px' }}>
                      {permission}
                    </span>
                  )) : <span style={{ fontSize: '12px', color: '#6b7280' }}>No permissions</span>}
                </div>
              )
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Active': 'badge-success',
                  'Inactive': 'badge-danger'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'lastModified',
              label: 'Last Modified',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            }
          ]}
          title="Access Policies"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('edit-policy', item)}
          onDelete={(item) => handleAction('edit-policy', item)}
          searchPlaceholder="Search policies by name, resource type, or conditions..."
          loading={false}
          emptyMessage="No access policies found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderAccessLogs = () => (
    <div>
      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#007B8F', fontWeight: '700', marginBottom: '4px' }}>
                    {accessLogs.length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Total Access Events
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#007B8F20',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={24} style={{ color: '#007B8F' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#28a745', fontWeight: '700', marginBottom: '4px' }}>
                    {accessLogs.filter(a => a.status === 'Granted').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Granted Access
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#28a74520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserCheck size={24} style={{ color: '#28a745' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#dc3545', fontWeight: '700', marginBottom: '4px' }}>
                    {accessLogs.filter(a => a.status === 'Denied').length}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Denied Access
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#dc354520',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <EyeOff size={24} style={{ color: '#dc3545' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '200px' }}>
          <div className="card h-100" style={{
            borderRadius: '16px',
            border: 'none',
            boxShadow: '0 4px 20px rgba(0, 123, 143, 0.1)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h3 style={{ color: '#6f42c1', fontWeight: '700', marginBottom: '4px' }}>
                    {new Set(accessLogs.map(a => a.user)).size}
                  </h3>
                  <p style={{ color: '#64748b', marginBottom: '0', fontSize: '14px' }}>
                    Unique Users
                  </p>
                </div>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#6f42c120',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Users size={24} style={{ color: '#6f42c1' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <DataTable
          data={accessLogs}
          columns={[
            {
              key: 'timestamp',
              label: 'Timestamp',
              width: '160px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'user',
              label: 'User',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'action',
              label: 'Action',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{value}</span>
              )
            },
            {
              key: 'resource',
              label: 'Resource',
              width: '150px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            },
            {
              key: 'status',
              label: 'Status',
              width: '100px',
              type: 'badge',
              badgeClass: (value) => {
                const statusClasses = {
                  'Granted': 'badge-success',
                  'Denied': 'badge-danger'
                };
                return statusClasses[value] || 'badge-secondary';
              }
            },
            {
              key: 'ipAddress',
              label: 'IP Address',
              width: '120px',
              render: (value) => (
                <span style={{ fontSize: '13px', fontFamily: 'monospace' }}>{value}</span>
              )
            },
            {
              key: 'reason',
              label: 'Reason',
              width: '200px',
              render: (value) => (
                <span style={{ fontSize: '13px' }}>{value}</span>
              )
            }
          ]}
          title="Access Logs"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={10}
          showActions={true}
          showCheckboxes={false}
          onView={(item) => handleAction('view-details', item)}
          onEdit={(item) => handleAction('export', item)}
          onDelete={(item) => handleAction('export', item)}
          searchPlaceholder="Search access logs by user, action, or resource..."
          loading={false}
          emptyMessage="No access logs found."
          className="shadow-lg"
        />
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'user-permissions':
        return renderUserPermissions();
      case 'role-management':
        return renderRoleManagement();
      case 'access-policies':
        return renderAccessPolicies();
      case 'access-logs':
        return renderAccessLogs();
      default:
        return renderUserPermissions();
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Access Controls
        </h1>
        <p className="page-subtitle">
          User permissions, role management, and access policy configuration
        </p>
      </div>

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map((tab) => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="page-tab-icon">
                  {tab.icon}
                </div>
                {tab.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        title={modalData?.action ? `Confirm ${modalData.action}` : 'Confirm Action'}
        message={modalData?.data ? `Are you sure you want to ${modalData.action} ${modalData.data.user || modalData.data.name || modalData.data.resource || 'this item'}?` : 'Are you sure you want to perform this action?'}
      />
    </div>
  );
};

export default AccessControls;
